/**
 * The item detail sheet (2026-09-04).
 *
 * User: "For each item, I should be able to read its usage and
 * characteristics. It should also be clear who the item is suitable for and
 * who it is not suitable for."
 *
 * ## What was actually missing
 *
 * Not descriptions — *nouns*. A curio's name was its id with the underscores
 * taken out ("Miners Pick"), a cosmetic's likewise ("Knight Gilded"), and
 * between them the four shelves offered a player exactly one line of prose:
 * an arms piece's `blurb`. Weight and price were on every card; what a thing
 * *is*, and whether it is for you, were on none of them.
 *
 * ## Why one sheet for four catalogues
 *
 * Curios, satchel charges, cosmetics and arms are four different tables with
 * four different shapes — but the question a player brings to all four is the
 * same three questions in the same order: what is it, what are its numbers,
 * should I take it. So the sheet is written once and each catalogue supplies
 * a `ItemDetailView`; a fifth shelf needs a builder, not a layout.
 *
 * ## Why the numbers are not in the prose
 *
 * `config/item-copy.ts` says it and it is worth repeating here, because this
 * is the file that would be tempting to break: every quantity on this sheet
 * is read off the live def at render time and none of it is written into the
 * copy. A rebalance in `consumable-defs.ts` or `WEAPONS` changes this sheet
 * with no edit here. The HUD has already shipped "110 / 100" once from
 * mirroring a server value by hand.
 *
 * ## The one number this sheet invents
 *
 * Curios get a **Wax per kg** row, which exists nowhere else in the game. It
 * is the entire loot decision in one figure — an Ale Keg pays 3 Wax a kilo
 * and a Warden's Seal pays 950 — and until now a player could only reach it
 * by dividing two numbers off two different cards.
 *
 * Pure string builders, same as the rest of `ui/`: every interesting state
 * here is worth a test that needs no hub and no server.
 */

import type { ItemDef, LootRarity } from '../config/item-defs';
import { waxPerKg } from '../config/item-defs';
import type { ItemCopy } from '../config/item-copy';
import type { ConsumableDefRow } from '../net/consumables-api';
import type { RiteDefRow } from '../net/rites-api';
import type { CosmeticDefRow } from '../net/cosmetics-api';
import type { EquipmentCatalogRow } from '../net/equipment-api';
import {
  itemTileMarkup, itemIconSrc, cosmeticIconSrc, consumableIconSrc, gearIconSrc,
  rarityTextClass, esc, riteIconSrc,
 UNKNOWN_PLATE_SRC,} from './item-icon';
import { consumableEffectLine } from './shop';
import { riteEffectLine, riteConditionLine, riteCapLine } from './rite-copy';
import { amountMarkup } from './currency';
import { iconMarkup } from './icon';
import { N_, t, getIntlLocale } from '../i18n/i18n';

/** A number with a fixed count of decimals, in the player's locale — `1.06` / `1,06`. */
function fixed(n: number, digits: number): string {
  return n.toLocaleString(getIntlLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Seconds, at most two decimals, in the player's locale — `1.5` / `1,5`. */
function secs(ms: number): string {
  return (ms / 1000).toLocaleString(getIntlLocale(), { maximumFractionDigits: 2 });
}

/**
 * An item's three lines, translated. Curios and the fallbacks are marked in
 * this repo's tables with N_(); the served catalogues' copy is marked in
 * `config/served-copy.ts`. Anything unmarked reads in English.
 */
function translatedCopy(copy: ItemCopy): ItemCopy {
  // A field an older server left out stays missing rather than throwing here.
  const tr = (line: string): string => (line ? t(line) : line);
  return { usage: tr(copy.usage), goodFor: tr(copy.goodFor), poorFor: tr(copy.poorFor) };
}

/** Class ids the roster knows, as words. */
const CLASS_NAME: Record<string, string> = {
  rogue: N_('Rogue'),
  knight: N_('Knight'),
  barbarian: N_('Barbarian'),
  druid: N_('Druid'),
};

/**
 * What the sheet says when the server did not send any words.
 *
 * Not defensive habit — a real crash. Running this against a backend that had
 * not yet reloaded the new catalogue threw `Cannot read properties of
 * undefined (reading 'usage')` and took the arms picker down with it. The
 * client does not choose which build of the server it talks to (a deploy is
 * two containers, and one of them is briefly older), so a missing field has to
 * degrade rather than break.
 */
const FALLBACK_COPY: Record<'curio' | 'satchel' | 'rite' | 'cosmetic' | 'arms', ItemCopy> = {
  curio: {
    usage: N_('A curio. It does nothing in the dungeon; you carry it out and the vendor buys it.'),
    goodFor: N_('A bag with room in it — check the Wax per kilo below against what else is on the shelf.'),
    poorFor: N_('A bag that is nearly full, if something lighter pays more for the same space.'),
  },
  satchel: {
    usage: N_('A satchel charge. Bought here, carried into a run, and lost if you die down there.'),
    goodFor: N_('The classes listed below; the shelf only shows what your delver can actually use.'),
    poorFor: N_('Any class not listed. Every charge is also a channel, so none of them is a panic button.'),
  },
  rite: {
    usage: N_('A Rite. Chosen before you descend, burned when the run starts, and answered when it ends.'),
    goodFor: N_('Read the condition below — every Rite pays on one kind of run and nothing on the others.'),
    poorFor: N_('A run you cannot finish. One is burned per run whatever happens, and none of them is refunded.'),
  },
  cosmetic: {
    usage: N_('A cosmetic. It changes how your delver looks and nothing else.'),
    goodFor: N_('Players who want to look like something in particular.'),
    poorFor: N_('Anyone looking for an advantage — no cosmetic in this game grants one.'),
  },
  arms: {
    usage: N_('A piece of arms. Weapons carry the numbers below; off-hands carry only their weight.'),
    goodFor: N_('Compare the numbers below against what you are holding now.'),
    poorFor: N_('Nothing here blocks, and no paid piece is stronger than a free one.'),
  },
};

/** One row of the characteristics table. */
export interface DetailStat {
  label: string;
  /** Plain text, escaped on render. Numbers arrive already formatted. */
  value: string;
}

export interface ItemDetailView {
  /**
   * What the sheet's own title bar says — the shelf, not the item.
   *
   * The item's name is four lines below in 17px; repeating it in the chrome
   * would spend the one line of the frame that could instead tell you which
   * shelf you are looking at, which is the thing a sheet opened from four
   * different places should say.
   */
  heading: string;
  /** The item's name, as the catalogue holds it. */
  title: string;
  /** "Curio · Rare · Vaults only" — what kind of thing this is, above the name. */
  eyebrow: string;
  iconSrc: string;
  /** Shown if `iconSrc` does not load — see `ItemTileOptions.fallbackSrc`. */
  iconFallbackSrc?: string;
  /** Drives the frame colour, exactly as it does on the card this was opened from. */
  rarity?: LootRarity;
  copy: ItemCopy;
  stats: DetailStat[];
  /** Already-built markup for the buy/sell/equip control, if the opener has one. */
  action?: string;
  /** One last line under everything — a cap, a warning, an ownership note. */
  note?: string;
  /**
   * A highlighted box above the numbers, for something true *today* that
   * changes what you should do with the thing.
   *
   * Exactly one thing uses it: a commission wanting this curio. That is the
   * point — a callout that appears on everything is decoration, and one that
   * appears when the answer to "should I sell this?" has changed is the
   * reason to open the sheet at all.
   */
  callout?: { title: string; body: string };
}

/** `Cellars and below` / `Ossuary and below` / `Vaults only` — where a curio can be found. */
export function depthLabel(minDepth: number): string {
  if (minDepth <= 1) return t('Every floor');
  if (minDepth === 2) return t('Ossuary and below');
  return t('Vaults only');
}

/** `rogue` -> `Rogue`, translated. An id this build does not know is capitalised as it stands. */
export function classLabel(classId: string): string {
  const name = CLASS_NAME[classId];
  return name ? t(name) : classId.charAt(0).toUpperCase() + classId.slice(1);
}

function statsMarkup(stats: readonly DetailStat[]): string {
  if (stats.length === 0) return '';
  const rows = stats.map((stat) => `
    <div class="flex items-baseline justify-between gap-3 py-1 border-b border-border/40 last:border-b-0">
      <dt class="text-[12px] text-text-dim shrink-0">${esc(stat.label)}</dt>
      <dd class="text-[12px] text-right tabular-nums">${esc(stat.value)}</dd>
    </div>`).join('');
  return `
    <section>
      <p class="eyebrow mb-1">${esc(t('Characteristics'))}</p>
      <dl class="frame-inset px-2.5 py-1">${rows}</dl>
    </section>`;
}

/**
 * The two verdicts.
 *
 * Given equal weight on purpose. A recommendation with the counter-case in
 * smaller type underneath is a recommendation with fine print, and the whole
 * reason `poorFor` is a required field is that the counter-case is usually
 * the more useful half — a player already suspects who an item is for.
 *
 * Marked with a glyph as well as a colour, for the same reason the loot grid
 * kept its rarity word next to its gold frame: colour alone is not readable
 * to everyone.
 */
function verdictsMarkup(copy: ItemCopy): string {
  return `
    <section class="grid gap-2 sm:grid-cols-2">
      <div class="frame-inset p-2.5 border-l-2 border-l-success">
        <p class="flex items-center gap-1.5 text-[12px] text-success mb-1">
          <span class="shrink-0" aria-hidden="true">${iconMarkup('check', 14)}</span>${esc(t('Suits'))}
        </p>
        <p class="text-[12px] text-text-dim leading-snug">${esc(copy.goodFor)}</p>
      </div>
      <div class="frame-inset p-2.5 border-l-2 border-l-warn">
        <p class="flex items-center gap-1.5 text-[12px] text-warn mb-1">
          <span class="shrink-0" aria-hidden="true">${iconMarkup('close', 14)}</span>${esc(t('Does not suit'))}
        </p>
        <p class="text-[12px] text-text-dim leading-snug">${esc(copy.poorFor)}</p>
      </div>
    </section>`;
}

/** Gold, because it is the one thing on the sheet that is true only today. */
function calloutMarkup(callout: { title: string; body: string }): string {
  return `
    <section class="frame-inset p-2.5 border-l-2 border-l-gold">
      <p class="text-[12px] text-gold mb-0.5">${esc(callout.title)}</p>
      <p class="text-[12px] text-text-dim leading-snug">${esc(callout.body)}</p>
    </section>`;
}

/** The whole sheet body. The overlay, the close button and the title bar are the hub's shell. */
export function itemDetailMarkup(view: ItemDetailView): string {
  return `
    <div class="flex flex-col gap-3">
      <div class="flex items-start gap-3">
        <span class="block w-20 shrink-0">${itemTileMarkup({
          src: view.iconSrc, fallbackSrc: view.iconFallbackSrc, alt: view.title, rarity: view.rarity,
        })}</span>
        <div class="min-w-0 flex-1">
          <p class="eyebrow ${view.rarity ? rarityTextClass(view.rarity) : ''}">${esc(view.eyebrow)}</p>
          <p class="font-heading text-[17px] leading-tight">${esc(view.title)}</p>
          <p class="text-[12px] text-text-dim leading-snug mt-1.5">${esc(view.copy.usage)}</p>
        </div>
      </div>
      ${view.callout ? calloutMarkup(view.callout) : ''}
      ${statsMarkup(view.stats)}
      ${verdictsMarkup(view.copy)}
      ${view.note ? `<p class="text-[11px] text-text-dim">${esc(view.note)}</p>` : ''}
      ${view.action ?? ''}
    </div>`;
}

/**
 * A curio.
 *
 * `sellWax` is passed rather than derived so this file never learns the
 * pricing rules — `sim/pricing.ts` owns them and the server owns the truth.
 *
 * `commission` is the reason this sheet is worth opening at all now. A curio
 * has one verb, Sell, and the number that decides whether to use it is the
 * Wax-per-kilo below — *unless* someone at the Sconce wants this exact thing
 * today, in which case holding it is worth more than selling it. That fact
 * lived on a different screen, which is the same as not existing.
 */
export function curioDetail(
  def: ItemDef,
  prices: { sellWax: number },
  opts: {
    action?: string;
    note?: string;
    commission?: { patron: string; title: string; count: number; held: number; payoutWax: number };
  } = {},
): ItemDetailView {
  const rarity = def.rarity === 'RARE' ? t('Rare') : t('Common');
  const stats: DetailStat[] = [
    { label: t('Rarity'), value: rarity },
    { label: t('Weight'), value: t('{kg} kg', { kg: fixed(def.weightKg, 1) }) },
    { label: t('Sells for'), value: t('{n} Wax', { n: prices.sellWax }) },
    { label: t('Wax per kg'), value: waxPerKg(def, prices.sellWax).toLocaleString(getIntlLocale()) },
    { label: t('Found on'), value: depthLabel(def.minDepth) },
  ];
  return {
    heading: t('Curio'),
    title: t(def.name),
    eyebrow: t('Curio · {rarity} · {depth}', { rarity, depth: depthLabel(def.minDepth) }),
    iconSrc: itemIconSrc(def.id),
    rarity: def.rarity,
    copy: translatedCopy(def.copy ?? FALLBACK_COPY.curio),
    stats,
    action: opts.action,
    note: opts.note,
    callout: opts.commission ? wantedCallout(def, opts.commission) : undefined,
  };
}

/** "Wanted today" — what the board is offering for this exact curio, and how close you are. */
export function wantedCallout(
  def: ItemDef,
  commission: { patron: string; title: string; count: number; held: number; payoutWax: number },
): { title: string; body: string } {
  const short = Math.max(0, commission.count - commission.held);
  const holding = short === 0
    ? t('You are holding enough — take them to the board.')
    : t('You have {held} of {count}; {short} more to go.', { held: commission.held, count: commission.count, short });
  // `x2 Warden's Key`, not `2 Warden's Keys`. An -s on a catalogue name is a
  // coin flip: "Tallow Candless", "Silver Services", "Warden's Regalias".
  return {
    title: t('Wanted today — {patron}', { patron: t(commission.patron) }),
    body: t('"{title}" pays {wax} Wax for {count} x {item}. {holding}', {
      title: t(commission.title), wax: commission.payoutWax, count: commission.count, item: t(def.name), holding,
    }),
  };
}

/** Every class that may drink it, named — or "Every class" when that is all of them. */
export function usableByLabel(usableBy: readonly string[], rosterSize: number): string {
  if (usableBy.length >= rosterSize) return t('Every class');
  return usableBy.map(classLabel).join(', ');
}

export function consumableDetail(
  def: ConsumableDefRow,
  opts: { owned: number; taking: number; rosterSize: number; action?: string; note?: string },
): ItemDetailView {
  const stats: DetailStat[] = [
    { label: t('Effect'), value: consumableEffectLine(def, { withChannel: false }) },
    { label: t('Time to use'), value: t('{s}s channel', { s: secs(def.channelMs) }) },
    { label: t('Cooldown'), value: t('{s}s', { s: Math.round(def.cooldownMs / 1000) }) },
    { label: t('Charges carried'), value: t('{n} per run', { n: def.maxCharges }) },
    { label: t('Weight'), value: t('{kg} kg each', { kg: fixed(def.weightKg, 1) }) },
    { label: t('Action bar'), value: t('Slot {n}', { n: def.slot }) },
    { label: t('Usable by'), value: usableByLabel(def.usableBy, opts.rosterSize) },
    { label: t('Price'), value: t('{n} Wax a charge', { n: def.priceWax }) },
    {
      label: t('You own'),
      value: opts.taking > 0 ? t('{owned} · taking {taking}', { owned: opts.owned, taking: opts.taking }) : `${opts.owned}`,
    },
  ];
  return {
    heading: t('Satchel charge'),
    title: t(def.name),
    eyebrow: t('Satchel · carried into a run, lost on death'),
    iconSrc: consumableIconSrc(def.id),
    copy: translatedCopy(def.copy ?? FALLBACK_COPY.satchel),
    stats,
    action: opts.action,
    note: opts.note,
  };
}

/**
 * A Rite (2026-09-07) — design/gdd/rites.md.
 *
 * The stat rows are ordered by what a buyer actually asks, in order: what does
 * it do, when does it pay, is there a ceiling on it, what does it cost, do I
 * have one. "Pays" sits second rather than last because it is the field that
 * distinguishes the four XP Rites from each other — they are the same shelf
 * item four times over until you read their conditions.
 */
export function riteDetail(
  def: RiteDefRow,
  opts: { owned: number; selected: boolean; action?: string; note?: string },
): ItemDetailView {
  const stats: DetailStat[] = [
    { label: t('Effect'), value: riteEffectLine(def).split(' \u00b7 ').slice(0, -1).join(' \u00b7 ') || t('None') },
    { label: t('Pays'), value: riteConditionLine(def) },
  ];
  const cap = riteCapLine(def);
  // Only when there is one. A "Ceiling: none" row is a row that teaches
  // nothing and pushes the price further down the sheet.
  if (cap) stats.push({ label: t('Ceiling'), value: cap });
  stats.push(
    { label: t('Burned'), value: t('One per run, whatever happens') },
    { label: t('Price'), value: t('{n} Wax each', { n: def.priceWax }) },
    { label: t('You own'), value: opts.selected ? t('{owned} · burning next run', { owned: opts.owned }) : `${opts.owned}` },
  );
  return {
    heading: t('Rite'),
    title: t(def.name),
    eyebrow: t('Rite · burned at the descent, paid at the end'),
    iconSrc: riteIconSrc(def.id),
    iconFallbackSrc: UNKNOWN_PLATE_SRC,
    copy: translatedCopy(def.copy ?? FALLBACK_COPY.rite),
    stats,
    action: opts.action,
    note: opts.note,
  };
}

/**
 * A skin or an emote.
 *
 * `name` and `copy` are optional on the wire (an older server sends neither),
 * so both fall back rather than rendering a blank sheet — the same reason
 * `cosmeticLabel` kept its id-prettifying path.
 */
export function cosmeticDetail(
  def: CosmeticDefRow,
  opts: { label: string; owned: boolean; equipped: boolean; action?: string; note?: string },
): ItemDetailView {
  const isEmote = def.kind === 'EMOTE';
  const stats: DetailStat[] = [
    { label: t('Type'), value: isEmote ? t('Emote') : t('Skin') },
    { label: t('Fits'), value: isEmote ? t('Any delver') : classLabel(String(def.appliesToClass)) },
    { label: t('Price'), value: t('{n} Ember', { n: def.priceEmber }) },
    { label: t('Effect on play'), value: t('None') },
    { label: t('You own it'), value: opts.owned ? (opts.equipped ? t('Yes — equipped') : t('Yes')) : t('No') },
  ];
  const copy: ItemCopy = translatedCopy(def.copy ?? FALLBACK_COPY.cosmetic);
  return {
    heading: isEmote ? t('Emote') : t('Skin'),
    title: def.name ? t(def.name) : opts.label,
    eyebrow: isEmote
      ? t('Cosmetic · Emote · Ember')
      : t('Cosmetic · Skin · {class}', { class: classLabel(String(def.appliesToClass)) }),
    iconSrc: cosmeticIconSrc(def.id),
    rarity: 'RARE',
    copy,
    stats,
    action: opts.action,
    note: opts.note,
  };
}

/**
 * A weapon or an off-hand.
 *
 * An off-hand gets a "Blocks" row reading "Nothing" rather than simply having
 * no defensive rows. A missing row is read as "not shown here"; a row saying
 * nothing is read as an answer, and this is the single most commonly wrong
 * assumption a player brings to this game from other ones.
 */
export function armsDetail(
  row: EquipmentCatalogRow,
  opts: { action?: string; note?: string } = {},
): ItemDetailView {
  const stats: DetailStat[] = [];
  if (row.slot === 'WEAPON') {
    stats.push(
      { label: t('Damage a swing'), value: `${row.damage ?? 0}` },
      { label: t('Swing'), value: t('{s}s', { s: fixed((row.swingMs ?? 0) / 1000, 2) }) },
      { label: t('Reach'), value: t('{n} m', { n: (row.reachM ?? 0).toLocaleString(getIntlLocale()) }) },
      { label: t('Stamina a swing'), value: `${row.staminaCost ?? 0}` },
      { label: t('Hands'), value: row.hands === 2 ? t('Both') : t('One') },
    );
  } else {
    stats.push({ label: t('Blocks'), value: t('Nothing') });
  }
  // Named rather than assumed (2026-09-07): the Chandlery arms are quoted in
  // Ember, and a sheet that said "Wax" would be the one place in the hub still
  // describing the old shelf.
  const premium = row.currency === 'PREMIUM';
  let cost: string;
  if (row.priceWax === undefined) cost = t('Comes with the account');
  else cost = premium ? t('{n} Ember', { n: row.priceWax }) : t('{n} Wax', { n: row.priceWax });
  stats.push(
    { label: t('Weight'), value: t('{kg} kg', { kg: row.totalWeightKg.toLocaleString(getIntlLocale()) }) },
    { label: t('Cost'), value: cost },
    { label: t('Unlocked'), value: row.owned ? t('Yes') : t('No') },
  );
  const kind = row.slot === 'WEAPON'
    ? (row.hands === 2 ? t('Weapon · Two-handed') : t('Weapon · One-handed'))
    : t('Off-hand · appearance only');
  let source: string;
  if (row.priceWax === undefined) source = t('Free');
  else source = premium ? t('Bought with Ember') : t('Bought with Wax');
  return {
    heading: row.slot === 'WEAPON' ? t('Weapon') : t('Off-hand'),
    title: t(row.name),
    eyebrow: t('{kind} · {source}', { kind, source }),
    iconSrc: gearIconSrc(row.model),
    copy: translatedCopy(row.copy ?? FALLBACK_COPY.arms),
    stats,
    action: opts.action,
    note: opts.note,
  };
}

/**
 * The buy control the sheet shows, so the detail is a place you can act from.
 *
 * Gold when you can afford it and stone when you cannot. `btn-gold` disabled
 * is gold text on a gold plate — legible enough on a page of them, unreadable
 * as the one control at the bottom of a sheet. Still a disabled button rather
 * than a missing one: hidden would read as "not for sale", which is a
 * different fact from "not today".
 */
export function detailBuyButton(dataAttr: string, id: string, labelMarkup: string, affordable: boolean): string {
  const cls = affordable ? 'btn btn-gold w-full' : 'btn btn-stone w-full';
  return `<button ${dataAttr}="${esc(id)}" class="${cls}"${affordable ? '' : ' disabled'}>${labelMarkup}</button>`;
}

/** `amountMarkup` re-exported through here so a builder's caller needs one import, not two. */
export { amountMarkup };
